import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import TopNav from "../components/TopNav";
import "./LedgerPage.css";


const PlaneIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5L21 16z"/>
    </svg>
);

const LedgerPage = (props) => {
    const [loading, setLoading] = useState(false)
    const [ledger, setLedger] = useState([])
    const [filterAccount, setFilterAccount] = useState('')
    const [filterDescrip, setFilterDescrip] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [expandedSummaries, setExpandedSummaries] = useState({ monthly: false, yearly: false })
    const itemsPerPage = 10

    const processedData = useMemo(() => {
        if (!ledger.length) return { entries: [] };
        const entries = ledger.map(item => ({
            ...item,
            date: new Date(item.epoch * 1000),
            formattedDate: new Date(item.epoch * 1000).toLocaleDateString(),
            formattedAmount: `$${item.amount.toFixed(2)}`
        })).sort((a, b) => b.date - a.date); // Sort by date descending
        return { entries };
    }, [ledger]);

    const filteredEntries = useMemo(() => {
        return processedData.entries.filter(entry =>
            entry.account.toLowerCase().includes(filterAccount.toLowerCase()) &&
            entry.descrip.toLowerCase().includes(filterDescrip.toLowerCase())
        );
    }, [processedData.entries, filterAccount, filterDescrip]);

    const filteredSummaries = useMemo(() => {
        const monthly = {};
        const yearly = {};
        filteredEntries.forEach(entry => {
            const year = entry.date.getFullYear();
            const month = entry.date.toLocaleString('default', { month: 'long' });
            const key = `${year}-${month}`;
            if (!monthly[key]) monthly[key] = { total: 0, count: 0 };
            monthly[key].total += entry.amount;
            monthly[key].count += 1;
            if (!yearly[year]) yearly[year] = { total: 0, count: 0 };
            yearly[year].total += entry.amount;
            yearly[year].count += 1;
        });
        return { monthly, yearly };
    }, [filteredEntries]);

    const paginatedEntries = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredEntries.slice(start, start + itemsPerPage);
    }, [filteredEntries, currentPage]);

    const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);

    useEffect(() => {
      window.scrollTo(0, 0);
      getLedger();

      return () => {}
    }, [])

    useEffect(() => {
      setCurrentPage(1); // Reset to first page when filters change
    }, [filterAccount, filterDescrip])
    

    const getLedger = async (obj) => {
        try {
            console.log("Get ledger")
            setLoading(true);

            const result = await axios.get("/api/user/get_ledger", {
                params: { _t: Date.now() }
            });                    

            console.log(result.data)
            setLedger(result.data); 

        }
        catch (e) {
            console.log(e)
            setLedger([]); 
        }
        finally{
            setLoading(false); // Stop loading after any error
        }
    }
  
  
    const themeClass = props.darkmode ? 'dark-theme' : 'light-theme';

    return(
        <div className={`ledger-page ${themeClass}`}>
            <TopNav page={props.page} darkMode={props.darkmode} />
            <div className="ledger-container">
                <header className="ledger-header">

                    <h1>Ledger</h1>
                </header>

                {loading && <div className="loading">Loading ledger data...</div>}

                {!loading && (
                    <>
                        {/* Summary Sections */}
                        <div className="summary-section">
                            <button
                                className="summary-toggle"
                                onClick={() => setExpandedSummaries(prev => ({ ...prev, yearly: !prev.yearly }))}
                                aria-expanded={expandedSummaries.yearly}
                            >
                                <PlaneIcon /> Yearly Summary {expandedSummaries.yearly ? '▼' : '▶'}
                            </button>
                            {expandedSummaries.yearly && (
                                <div className="summary-content">
                                    {Object.entries(filteredSummaries.yearly).map(([year, data]) => (
                                        <div key={year} className="summary-card">
                                            <h3>{year}</h3>
                                            <p>Total: ${data.total.toLocaleString()}</p>
                                            <p>Transactions: {data.count}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="summary-section">
                            <button
                                className="summary-toggle"
                                onClick={() => setExpandedSummaries(prev => ({ ...prev, monthly: !prev.monthly }))}
                                aria-expanded={expandedSummaries.monthly}
                            >
                                <PlaneIcon /> Monthly Summary {expandedSummaries.monthly ? '▼' : '▶'}
                            </button>
                            {expandedSummaries.monthly && (
                                <div className="summary-content">
                                    {Object.entries(filteredSummaries.monthly).map(([key, data]) => (
                                        <div key={key} className="summary-card">
                                            <h3>{key.replace('-', ' ')}</h3>
                                            <p>Total: ${data.total.toLocaleString()}</p>
                                            <p>Transactions: {data.count}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Filters */}
                        <div className="filters">
                            <input
                                type="text"
                                placeholder="Filter by Account"
                                value={filterAccount}
                                onChange={(e) => setFilterAccount(e.target.value)}
                                className="filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter by Description"
                                value={filterDescrip}
                                onChange={(e) => setFilterDescrip(e.target.value)}
                                className="filter-input"
                            />
                        </div>

                        {/* Table */}
                        <div className="ledger-table-container">
                            <table className="ledger-table" role="table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Account</th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedEntries.map((entry, index) => (
                                        <tr key={index}>
                                            <td>{entry.formattedDate}</td>
                                            <td>{entry.account}</td>
                                            <td>{entry.descrip}</td>
                                            <td className={`amount ${entry.amount >= 0 ? 'positive' : 'negative'}`}>{entry.formattedAmount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="pagination">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
export default LedgerPage;